import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { updateEnvelope, createSmartQuestionaire } from "../../../services/CommonService";
import { toast } from "react-toastify";
import Utils from "../../../utils";
import DatePicker from "react-datepicker";
import { DATE_FORMAT_LIST } from "../../../configs/AppConfig";
import '../../../assets/sleeve.css';
import Select from 'react-select';
import validator from "validator";
import { getRecipientList } from "../../../services/Recipients";
import EnvelopeRecipients from "../Envelope/create/Recipients";

function SmartQuestionaireStep1(props) {
  const navigate = useNavigate();
  const { uuid } = useParams();
  const [pastDate, setPastDate] = useState(new Date());
  const [senderList, setSenderList] = useState([]);
  const [envelopeSenderId, setEnvelopeSenderId] = useState(0);
  const [envelopeDate, setEnvelopeDate] = useState('');
  const [smartFormName, setSmartFormName] = useState("");
  const dueDateRef = useRef(null);
  const buttonRef = useRef(null);

  // Recipient search state
  const [searchRecipientList, setSearchRecipientList] = useState([]);
  const [recipientSearch, setRecipientSearch] = useState("");
  const [recipientList, setRecipientList] = useState([]);
  const [recipientError, setRecipientError] = useState("");

  // Recipient add/edit state
  const [docData, setDocData] = useState({
    id: 0,
    first_name: "",
    last_name: "",
    email: "",
    mobile: "",
    country_id: 227,
    company_name: "",
    address_1: "",
    address_2: "",
    city: "",
    state: "",
    address_country_id: "",
    zip_code: "",
  });

  // Function to clear recipient form data
  const clearRecipientFormData = () => {
    setDocData({
      id: 0,
      first_name: "",
      last_name: "",
      email: "",
      mobile: "",
      country_id: 227,
      company_name: "",
      address_1: "",
      address_2: "",
      city: "",
      state: "",
      address_country_id: "",
      zip_code: "",
    });
  };

  // Function to handle form object updates from EnvelopeRecipients
  const handleFormObjUpdate = (formData) => {
    if (formData && formData.recipient_List) {
      setRecipientList(formData.recipient_List || []);
    }
  };

  // Prepare recipient options for react-select
  const recipientOptions = searchRecipientList.map(item => ({
    value: item.email,
    label: `${item.first_name} ${item.last_name} (${item.email})`,
    data: item,
  }));

  // Custom option rendering for react-select
  const formatRecipientOption = (option) => (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "8px 12px",
        borderRadius: 8,
        minWidth: 0,
        width: "100%",
        boxSizing: "border-box",
      }}
    >
      <div className="avatar-circle-custom">
        {option.data.first_name?.[0]?.toUpperCase() || ""}
      </div>
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            fontWeight: 600,
            fontSize: 16,
            color: "#222",
            height: "auto",
            maxWidth: 180,
          }}
        >
          {option.data.first_name + " " + option.data.last_name}
        </div>
        <div className="recipient-email-mobile-container">
          <div className="recipient-email">{option.data.email}</div>
          {option.data.mobile && (
            <div className="recipient-mobile-under-email">
              {Utils.mobileFormat(option.data)}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // react-select onChange handler
  const handleSelectRecipient = (option) => {
    if (!option) return;
    const obj = option.data;
    if (recipientList.find(r => r.email === obj.email)) {
      setRecipientError("Recipient already exists");
      return;
    }
    setRecipientList([...recipientList, obj]);
    setRecipientSearch("");
    setSearchRecipientList([]);
    setRecipientError("");
  };

  let errorsObj = {
    sender_id: '',
    envelope_date: '',
    smart_form_name: '',
    recipient: '',
  };
  const [errors, setErrors] = useState(errorsObj);

  // Helper to get initials from name
  function getInitials(firstName, lastName) {
    if (!firstName && !lastName) return '';
    return ((firstName ? firstName[0] : '') + (lastName ? lastName[0] : '')).toUpperCase();
  }

  useEffect(() => {
    setPastDate(Utils.pastDate());
    if (props?.senderList && props.senderList.length > 0) {
      setSenderList(props.senderList);
    }
    const formObj = props.formObj || {};
    let initialSenderId = formObj.sender_id || 0;
    if (!initialSenderId && props?.senderList && props.senderList.length > 0) {
      let index = props?.senderList.findIndex(x => x.is_login_user === true);
      if (index > -1) {
        initialSenderId = props.senderList[index]['id'];
      }
    }
    setEnvelopeSenderId(Number(initialSenderId));
    setEnvelopeDate(
      formObj.envelope_date
        ? new Date(formObj.envelope_date)
        : formObj.due_date
          ? new Date(formObj.due_date)
          : null
    ); // always Date or null
    setSmartFormName(formObj.smart_form_name || '');
    setRecipientList(formObj.recipient_list || []); // <-- use recipient_list
  }, [props]);

  function onBack(e) {
    e.preventDefault();
    navigate('/client-portal');
  }

  function onSaveExit(e) {
    e.preventDefault();
    onSubmitForm(1);
  }

  function onNext(e) {
    e.preventDefault();
    onSubmitForm(2);
  }

  // Recipient search logic (like EnvelopeStep2)
  const handleRecipientInputChange = (inputValue) => {
    setRecipientSearch(inputValue);
    getRecipientList({ search: inputValue, limit: 50, page: 1 })
      .then((response) => {
        setSearchRecipientList(response.data?.data || []);
      })
      .catch(() => setSearchRecipientList([]));
  };

  const handleRecipientInputFocus = () => {
    if (recipientSearch === "" && searchRecipientList.length === 0) {
      getRecipientList({ search: "", limit: 50, page: 1 })
        .then((response) => {
          setSearchRecipientList(response.data?.data || []);
        })
        .catch(() => setSearchRecipientList([]));
    }
  };

  const handleSearchRecipient = (e, obj) => {
    e.preventDefault();
    if (recipientList.find(r => r.email === obj.email)) {
      setRecipientError("Recipient already exists");
      return;
    }
    setRecipientList([...recipientList, obj]);
    setRecipientSearch("");
    setSearchRecipientList([]);
    setRecipientError("");
  };

  function removeRecipient(index) {
    setRecipientList(recipientList.filter((_, i) => i !== index));
  }

  function onEditRecipient(e, obj) {
    e.preventDefault();
    let formData = { ...docData };
    formData.id = obj.id;
    formData.first_name = obj.first_name;
    formData.last_name = obj.last_name;
    formData.email = obj.email;
    formData.mobile = obj.mobile;
    formData.country_id = obj.country_id;
    formData.company_name = obj.company_name ? obj.company_name : "";
    formData.address_1 = obj.address_1 ? obj.address_1 : "";
    formData.address_2 = obj.address_2 ? obj.address_2 : "";
    formData.city = obj.city ? obj.city : "";
    formData.state = obj.state ? obj.state : "";
    formData.zip_code = obj.zip_code ? obj.zip_code : "";
    formData.address_country_id = obj.address_country_id ? obj.address_country_id : "";
    setDocData(formData);
    buttonRef.current?.click();
  }

  function onSubmitForm(type) {
    let error = false;
    const errorObj = { ...errorsObj };
    if (!envelopeSenderId) {
      errorObj.sender_id = 'Please select sender';
      error = true;
    }
    if (!smartFormName) {
      errorObj.smart_form_name = 'Questionnaire Name is required';
      error = true;
    }
    if (!envelopeDate) {
      errorObj.envelope_date = 'Please select date';
      error = true;
    }
    if (recipientList.length === 0) {
      errorObj.recipient = 'Please add at least one recipient';
      error = true;
    }
    setErrors(errorObj);
    if (error) return;
    // Only keep relevant fields
    let recipientIds = recipientList.map(r => r.id).join(",");
    let obj = {
      sleeve_name: smartFormName,
      due_date: envelopeDate
        ? (typeof envelopeDate === 'string'
          ? envelopeDate
          : envelopeDate instanceof Date
            ? envelopeDate.getFullYear() + '-' +
            String(envelopeDate.getMonth() + 1).padStart(2, '0') + '-' +
            String(envelopeDate.getDate()).padStart(2, '0')
            : '')
        : '',
      recipient_ids: recipientIds,
    };
    // Yahan par edit mode check karo
    if (props.formObj && props.formObj.smart_questionaire_uuid) {
      obj.sleeve_uuid = props.formObj.smart_questionaire_uuid;
    }
    props.setLoading(true);
    createSmartQuestionaire(obj)
      .then(response => {
        if (type === 2) {
          // Update formObj with new data
          let formData = { ...props.formObj };
          formData.smart_questionaire_id = response.data.id;
          formData.smart_questionaire_uuid = response.data.uuid;
          formData.current_step = 2;
          formData.sender_id = envelopeSenderId;
          formData.smart_form_name = smartFormName;
          formData.due_date = envelopeDate;
          formData.recipient_list = recipientList;
          props.setFormObj(formData);

          // Navigate to step 2 with URL
          navigate(`/client-portal/smart-questionnaire/${response.data.uuid}/step/2`);
        } else {
          navigate('/client-portal');
        }
        toast.success(response.data.message);
        props.setLoading(false);
      })
      .catch(err => {
        toast.error(Utils.getErrorMessage(err));
        props.setLoading(false);
      });
  }
  useEffect(() => {
    console.log("Body Class List in StepX:", document.body.classList);
  }, []);

  const showDate = (date) => {
    let dateFormat = '';
    if (date) {
      dateFormat = new Date(date);
    }
    return dateFormat;
  };

  const showDateFormat = (id) => {
    let value = '';
    let index = DATE_FORMAT_LIST.findIndex(x => parseInt(x.id) === parseInt(id));
    if (index > -1) {
      value = DATE_FORMAT_LIST[index]['format'];
    }
    return value;
  };

  const showDatePlaceholder = (id) => {
    let value = '';
    let index = DATE_FORMAT_LIST.findIndex(x => parseInt(x.id) === parseInt(id));
    if (index > -1) {
      value = DATE_FORMAT_LIST[index]['text'];
    }
    return value;
  };

  // Prepare sender options for react-select
  const senderOptions = senderList.map(item => ({
    value: Number(item.id),
    label: `${item.first_name} ${item.last_name} (${item.email})`,
    data: item,
  }));
  const selectedSenderOption = senderOptions.find(opt => opt.value === Number(envelopeSenderId)) || null;

  useEffect(() => {
    // Only run if Bootstrap is loaded
    if (window.bootstrap && window.bootstrap.Tooltip) {
      const tooltipTriggerList = [].slice.call(document.querySelectorAll('[data-bs-toggle="tooltip"]'));
      tooltipTriggerList.forEach(function (tooltipTriggerEl) {
        // Dispose previous tooltip if exists
        if (tooltipTriggerEl._tooltipInstance) {
          tooltipTriggerEl._tooltipInstance.dispose();
        }
        tooltipTriggerEl._tooltipInstance = new window.bootstrap.Tooltip(tooltipTriggerEl, { trigger: 'hover focus click' });
      });
    }
  });

  return (
    <>
      <div className="sleeve-step mt-3 d-flex justify-content-center">
        <div className="sleeve-main-card pt-4">
          <h1 className="sleeve-title">Smart Questionnaire</h1>
          {/* <p className="sleeve-subtitle">
            Create smart Questionnaire.
          </p> */}
          {/* Sender Section */}
          <div className="sleeve-sender-card">
            <h2 className="sleeve-heading">
              <i className="bi bi-person" />
              <span>
                Your Email Address (Sender)
                <i
                  className="bi bi-info-circle ms-1 text-muted cursor-pointer fs-6"
                  aria-hidden="true"
                  data-toggle="tooltip"
                  data-placement="left"
                  title="This is the email used to send the smart Questionnaire. You can select from your verified addresses."
                />
              </span>
            </h2>
            <Select
              classNamePrefix="sleeve-sender-select"
              value={selectedSenderOption}
              onChange={(option) =>
                setEnvelopeSenderId(option ? Number(option.value) : 0)
              }
              options={senderOptions}
              placeholder="Select sender"
              isSearchable
              formatOptionLabel={(option) => (
                <div className="sleeve-sender-option">
                  <div className="sleeve-sender-initials">
                    {getInitials(option.data.first_name, option.data.last_name)}
                  </div>
                  <div>
                    <div className="sleeve-sender-name">
                      {option.data.first_name} {option.data.last_name}
                    </div>
                    <div className="sleeve-sender-email">
                      {option.data.email}
                    </div>
                  </div>
                </div>
              )}
              styles={{}}
            />
            {errors.sender_id && (
              <div className="error-message">{errors.sender_id}</div>
            )}
          </div>

          {/* Smart Form Name and Due Date Section Inline */}
          <div className="sleeve-sender-card">
            <div className="row g-3">
              {/* Smart Form Name */}
              <div className="col-lg-7">
                <label className="sleeve-step-label">
                  Questionnaire Name
                  <i
                    className="bi bi-info-circle ms-1 text-muted cursor-pointer"
                    aria-hidden="true"
                    data-toggle="tooltip"
                    data-placement="right"
                    title="Enter Questionnaire Name"
                  />
                </label>
                <input
                  type="text"
                  className={`sleeve-step-input ${errors.smart_form_name ? "input-error" : ""}`}
                  placeholder="Enter Questionnaire Name"
                  value={smartFormName}
                  onChange={(e) => setSmartFormName(e.target.value)}
                />
                {errors.smart_form_name && (
                  <div className="input-error-message">{errors.smart_form_name}</div>
                )}
              </div>
              {/* Due Date */}
              <div className="col-lg-5">
                <label className="sleeve-step-label">
                  Submission Deadline (Due Date)
                  <i
                    className="bi bi-info-circle ms-1 text-muted cursor-pointer"
                    aria-hidden="true"
                    data-toggle="tooltip"
                    data-placement="right"
                    title="The recipient should submit Questionnaire answers by this date. Optional, but helps set expectations."
                  />
                </label>
                <div className="position-relative">
                  <DatePicker
                    ref={dueDateRef}
                    selected={envelopeDate}
                    onChange={(date) => setEnvelopeDate(date)}
                    minDate={pastDate}
                    dateFormat={showDateFormat(1)}
                    placeholderText="dd-mm-yyyy"
                    className={`sleeve-step-input ${errors.envelope_date ? "input-error" : ""}`}
                  />
                  <i
                    className="bi bi-calendar"
                    style={{
                      position: "absolute",
                      right: "16px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      cursor: "pointer",
                      color: "#888",
                    }}
                    onClick={() => dueDateRef?.current.setFocus(true)}
                  />
                </div>
                {errors.envelope_date && (
                  <div className="input-error-message">{errors.envelope_date}</div>
                )}
              </div>
            </div>
          </div>
          {/* Recipient Section (EnvelopeStep2 style) */}
          <div className="sleeve-sender-card mb-4">
            <div className="recipient-details-card-custom-add">
              <div className="align-items-center gap-2">
                <i className="bi bi-people me-2 fs-4" />
                <span className="fw-bold fs-5 sleeve-title-color">Recipient Details</span>
                <i className="bi bi-info-circle ms-1 cur-pointer fs-5" aria-hidden="true" data-toggle="tooltip"
                  data-placement="left" title={"Add Recipient(s)\n\nEnter one or more email addresses of people from whom you need Questionnaire. Each will receive a personal link."} />
              </div>
              <div className="align-items-center gap-2">
                <button
                  type="button"
                  className="step2-add-btn ms-auto"
                  data-bs-toggle="offcanvas"
                  ref={buttonRef}
                  data-bs-target="#addRecipients"
                  aria-controls="addRecipients"
                  data-toggle="tooltip"
                  title="Add Recipients"
                >
                  <span className="me-2">+</span>Add Recipients
                </button>
              </div>
            </div>
            <div className="mb-3 position-relative">
              <Select
                classNamePrefix="sleeve-sender-select"
                value={null}
                inputValue={recipientSearch}
                onInputChange={value => { setRecipientSearch(value); handleRecipientInputChange(value); }}
                onFocus={handleRecipientInputFocus}
                onChange={handleSelectRecipient}
                options={recipientOptions}
                placeholder="Search recipients..."
                isClearable
                isSearchable
                formatOptionLabel={formatRecipientOption}
                styles={{
                  control: (base) => ({
                    ...base,
                    borderRadius: 8,
                    minHeight: 48,
                    borderColor: errors.recipient ? '#dc3545' : '#e3e8f0',
                    boxShadow: 'none',
                    background: '#fff'
                  }),
                  option: (base, state) => ({
                    ...base,
                    background: state.isFocused ? '#f3f4f6' : '#fff',
                    color: '#222',
                    cursor: 'pointer'
                  }),
                  menu: (base) => ({ ...base, zIndex: 99 }),
                  input: (base) => ({ ...base, fontSize: '1rem' }),
                }}
              />
              {errors.recipient && (
                <div className="text-danger mt-2">{errors.recipient}</div>
              )}
              {recipientError && (
                <div className="text-danger mt-2">{recipientError}</div>
              )}
            </div>
            {/* Recipient List or Empty State */}
            {recipientList.length === 0 ? (
              <div className="step2-empty-state">
                <div className="icon"><i className="bi bi-people" /></div>
                <div>No recipients added yet</div>
                <div style={{ fontSize: '13px' }}>Search and add recipients to get started</div>
              </div>
            ) : (
              <div className="gap-3 w-100">
                {recipientList.map((item, index) => (
                  <div className="recipient-list-item-custom d-flex align-items-center justify-content-between bg-white rounded-3 px-4 py-3 shadow-sm" key={index}>
                    <div className="d-flex align-items-center gap-3">
                      <div className="avatar-circle-custom">{item.first_name?.[0]?.toUpperCase() || ''}</div>
                      <div>
                        <div className="fw-bold recipient-name">{item.first_name + ' ' + item.last_name}</div>
                        <div className="recipient-email-mobile">
                          <span className="text-muted">{item.email}</span>
                          <span className="text-muted recipient-mobile">{Utils.mobileFormat(item)}</span>
                        </div>
                      </div>
                    </div>
                    <div className="d-flex align-items-center gap-3">
                      <span className="text-primary fs-5 cursor-pointer cur-pointer" onClick={(e) => onEditRecipient(e, item)}>
                        <i className="bi bi-pencil-square" />
                      </span>
                      <span className="text-danger fs-5 cursor-pointer cur-pointer" onClick={() => removeRecipient(index)}>
                        <i className="bi bi-x-lg cursor-pointer cur-pointer" />
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Recipient Add/Edit Offcanvas */}
          <EnvelopeRecipients
            formObj={{ recipient_List: recipientList }}
            setFormObj={handleFormObjUpdate}
            setLoading={props.setLoading}
            recipData={docData}
            setDocData={setDocData}
            onClose={clearRecipientFormData}
          />
          {/* Buttons */}
          <div className="sleeve-step-buttons">
            <div>
              <button className="sleeve-btn-back" onClick={onBack}>
                Back
              </button>
            </div>
            <div className="sleeve-step-buttons-right">
              {/* <button className="sleeve-btn-save" onClick={onSaveExit}>
                Save & Exit
              </button> */}
              <button className="sleeve-btn-next" onClick={onNext}>
                Next
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
export default SmartQuestionaireStep1; 